import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Blocks, X, Loader2, Plus, Package, Trash2, Save, Star } from "lucide-react";

export default function AssembliesManager({ open, onClose, onApply, boq }) {
  const qc = useQueryClient();
  const [qty, setQty] = useState({});
  const [saveName, setSaveName] = useState("");
  const [saveScope, setSaveScope] = useState("all");
  const [saving, setSaving] = useState(false);
  const { data: assemblies = [], isLoading } = useQuery({ queryKey: ["assemblies"], queryFn: async () => (await api.get(`/assemblies`)).data, enabled: open });

  const sections = boq?.sections || [];
  const canSave = sections.some((s) => (s.items || []).length > 0);

  const saveFromBoq = async () => {
    if (!saveName.trim()) { toast.error("Name your assembly"); return; }
    setSaving(true);
    try {
      await api.post(`/assemblies/from-boq`, {
        boq_id: boq.id, name: saveName.trim(), unit: "ea",
        section_code: saveScope === "all" ? null : saveScope,
      });
      setSaveName("");
      qc.invalidateQueries({ queryKey: ["assemblies"] });
      toast.success("Saved as reusable assembly");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/assemblies/${id}`); qc.invalidateQueries({ queryKey: ["assemblies"] }); toast.success("Removed"); } catch (e) { toast.error(apiErr(e)); } };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="assemblies-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Blocks className="h-4 w-4 text-primary" /> Assemblies Library</h3>
          <button data-testid="assemblies-close-btn" onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary text-muted-foreground"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Drop a whole assembly into the estimate in one click, or save part of this estimate as a reusable assembly for future bids.</p>

        {boq && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-4" data-testid="save-assembly-box">
            <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Save className="h-3.5 w-3.5 text-primary" /> Save from this estimate</div>
            <div className="flex flex-wrap gap-2">
              <input data-testid="save-assembly-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Assembly name (e.g. Standard bathroom)" className="flex-1 min-w-[160px] rounded-lg border border-input bg-popover px-3 py-2 text-sm outline-none focus:border-primary" />
              <select data-testid="save-assembly-scope" value={saveScope} onChange={(e) => setSaveScope(e.target.value)} className="rounded-lg border border-input bg-popover px-2 py-2 text-sm outline-none focus:border-primary">
                <option value="all">Whole estimate</option>
                {sections.filter((s) => (s.items || []).length).map((s) => <option key={s.code} value={s.code}>Div {s.code} · {s.title}</option>)}
              </select>
              <button data-testid="save-assembly-btn" onClick={saveFromBoq} disabled={saving || !canSave} className="rounded-lg bg-primary text-primary-foreground px-3 py-2 font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-60 inline-flex items-center gap-1">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-2" data-testid="assemblies-list">
            {assemblies.map((a) => (
              <div key={a.id} className="rounded-lg border border-border/60 p-3" data-testid={`assembly-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {a.custom ? <Star className="h-3.5 w-3.5 text-amber-500 shrink-0" /> : <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      {a.name}
                      {a.custom && <span className="text-[9px] font-mono uppercase tracking-wider text-amber-500 border border-amber-500/40 rounded px-1">Custom</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.description}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{a.items.length} items · per {a.unit}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <input data-testid={`assembly-qty-${a.id}`} type="number" value={qty[a.id] ?? ""} onChange={(e) => setQty({ ...qty, [a.id]: e.target.value })}
                      placeholder="Qty" className="w-16 rounded-lg border border-input bg-popover px-2 py-1.5 text-sm font-mono text-right outline-none focus:border-primary" />
                    <span className="text-xs text-muted-foreground w-5">{a.unit}</span>
                    <button data-testid={`assembly-add-${a.id}`} onClick={() => { onApply(a, Number(qty[a.id]) || 1); }}
                      className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 font-semibold text-sm hover:bg-orange-600 transition-colors inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Add</button>
                    {a.custom && <button data-testid={`assembly-delete-${a.id}`} onClick={() => del(a.id)} className="h-8 w-8 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
